import type {
  BindablePropertyDef,
  BindingPath,
  GeneralFieldType,
  GeneralMeta,
} from '@packages/systems/dynamo/source-types';
import type {EntityTypeReference, ListTypeReference} from './type';

// Opaque type removed - see /packages/utilities/type-safety/README.md for more information
/** Internal type */

export type BindableProperty = BindablePropertyDef<
  EntityTypeReference,
  ListTypeReference
>;

/** Constructor for internal type */

export function buildBindableProperty(
  definition: BindablePropertyDef<EntityTypeReference, ListTypeReference>
): BindableProperty {
  return definition;
}

/** Binding Source type */

export type BindingSource = BindableProperty & {
  rendering: NonNullable<BindableProperty['rendering']>;
  parentId: string | null;
};

/** toBindingSource */

const defaultRendering = {
  strategy: 'server',
} as const;

type BindingSourceBase = {
  id: BindingSource['id'];
  label: BindingSource['label'];
  path: BindingSource['path'];
  queryPath: BindingSource['queryPath'];
  rendering: BindingSource['rendering'];
  parentId: BindingSource['parentId'];
  groupLabel?: BindingSource['groupLabel'];
  deprecated?: BindingSource['deprecated'];
};

export function toBindingSource(
  bindableProperty: BindableProperty
): BindingSource {
  const {
    id,
    label,
    path,
    queryPath,
    rendering = defaultRendering,
    groupLabel,
    deprecated,
  } = bindableProperty;

  const base: BindingSourceBase = {
    id,
    label,
    path,
    queryPath,
    rendering,
    parentId: null,
  };

  if (groupLabel) {
    base.groupLabel = groupLabel;
  }

  if (deprecated) {
    base.deprecated = deprecated;
  }

  switch (bindableProperty.type) {
    case 'ItemRef': {
      return {
        type: 'ItemRef',
        meta: bindableProperty.meta,
        ...base,
      };
    }
    case 'ItemRefSet': {
      return {
        type: 'ItemRefSet',
        meta: bindableProperty.meta,
        ...base,
      };
    }
    case 'Set': {
      return {
        type: 'Set',
        meta: bindableProperty.meta,
        ...base,
      };
    }
    case 'Number': {
      return {
        type: 'Number',
        meta: bindableProperty.meta,
        ...base,
      };
    }
    case 'Date': {
      return {
        type: 'Date',
        meta: bindableProperty.meta,
        ...base,
      };
    }
    case 'PlainText': {
      return {
        type: 'PlainText',
        meta: bindableProperty.meta,
        ...base,
      };
    }
    case 'RichText': {
      return {
        type: 'RichText',
        meta: bindableProperty.meta,
        ...base,
      };
    }
    case 'Option': {
      return {
        type: 'Option',
        meta: bindableProperty.meta,
        ...base,
      };
    }
    case 'Slug': {
      return {
        type: 'Slug',
        meta: bindableProperty.meta,
        ...base,
      };
    }

    default: {
      return {
        type: bindableProperty.type as GeneralFieldType,
        meta: bindableProperty.meta as GeneralMeta,
        ...base,
      };
    }
  }
}

export type BindingSourceExtendableProps = {
  id?: string;
  parentId?: string | null;
  label?: string;
  groupLabel?: string;
  path?: BindingPath;
  queryPath?: string[];
  deprecated?: true;
};

export function extendBindingSource(
  bindingSource: BindingSource,
  nextProps: BindingSourceExtendableProps
): BindingSource {
  const {id, label, parentId, path, queryPath, rendering, deprecated} =
    bindingSource;

  const base: BindingSourceBase = {
    id,
    label,
    parentId,
    path,
    queryPath,
    rendering,
  };

  if (deprecated) {
    base.deprecated = deprecated;
  }

  switch (bindingSource.type) {
    case 'ItemRef': {
      return {
        type: 'ItemRef',
        meta: bindingSource.meta,
        ...base,
        ...nextProps,
      };
    }
    case 'ItemRefSet': {
      return {
        type: 'ItemRefSet',
        meta: bindingSource.meta,
        ...base,
        ...nextProps,
      };
    }
    case 'Set': {
      return {
        type: 'Set',
        meta: bindingSource.meta,
        ...base,
        ...nextProps,
      };
    }
    case 'Number': {
      return {
        type: 'Number',
        meta: bindingSource.meta,
        ...base,
        ...nextProps,
      };
    }
    case 'Date': {
      return {
        type: 'Date',
        meta: bindingSource.meta,
        ...base,
        ...nextProps,
      };
    }
    case 'PlainText': {
      return {
        type: 'PlainText',
        meta: bindingSource.meta,
        ...base,
        ...nextProps,
      };
    }
    case 'RichText': {
      return {
        type: 'RichText',
        meta: bindingSource.meta,
        ...base,
        ...nextProps,
      };
    }
    case 'Option': {
      return {
        type: 'Option',
        meta: bindingSource.meta,
        ...base,
        ...nextProps,
      };
    }
    case 'Slug': {
      return {
        type: 'Slug',
        meta: bindingSource.meta,
        ...base,
        ...nextProps,
      };
    }
    default: {
      return {
        type: bindingSource.type as GeneralFieldType,
        meta: bindingSource.meta as GeneralMeta,
        ...base,
        ...nextProps,
      };
    }
  }
}
